import {
  digitsOnly,
  parsePhoneValue,
  combinePhone,
  DEFAULT_PHONE_DIAL,
} from './phone';
import { isFutbolOnlyConsumer } from './futbolModule';

const DEFAULT_SUPPORT_PHONE = '573192635819';
const FUTBOL_SUPPORT_PHONE = String(
  import.meta.env.VITE_FUTBOL_SUPPORT_WHATSAPP || '3152521581',
).trim() || '3152521581';

const DEFAULT_MESSAGES = {
  vital: 'Hola, necesito ayuda con mi programa Vital.',
  pancitas: 'Hola, necesito ayuda con Pancitas Fit.',
  virtual_d28d: 'Hola, necesito ayuda con mi programa Virtual.',
  d28d: 'Hola, necesito ayuda con Fit Live · Programa.',
  food: 'Hola, necesito ayuda con mi plan de alimentación.',
  training: 'Hola, necesito soporte de mi cuenta de entrenador.',
  futbol: 'Hola, necesito ayuda con futbol.app.',
};

function digitsOnlyLocal(phone) {
  return digitsOnly(phone);
}

function isPlaceholderPhone(phone) {
  const d = digitsOnlyLocal(phone);
  if (!d) return true;
  if (/^0+$/.test(d)) return true;
  if (/^570+$/.test(d)) return true;
  if (/^5730{6,}$/.test(d)) return true;
  if (/^30{6,}$/.test(d)) return true;
  return false;
}

export function normalizePhone(phone) {
  const d = digitsOnlyLocal(phone);
  if (!d || isPlaceholderPhone(d)) return DEFAULT_SUPPORT_PHONE;
  if (d.length === 10 && d.startsWith('3')) return `57${d}`;
  return d;
}

export { digitsOnly, parsePhoneValue, combinePhone, DEFAULT_PHONE_DIAL };

export function defaultMessageForPlan(plan = {}) {
  const programId = String(plan.program_id || '').toLowerCase();
  const kind = String(plan.kind || 'd28d').toLowerCase();
  if (plan.support_message && String(plan.support_message).trim()) {
    return String(plan.support_message).trim();
  }
  if (DEFAULT_MESSAGES[programId]) return DEFAULT_MESSAGES[programId];
  if (DEFAULT_MESSAGES[kind]) return DEFAULT_MESSAGES[kind];
  return DEFAULT_MESSAGES.d28d;
}

export function buildWaMeUrl(phone, message) {
  const num = normalizePhone(phone);
  const text = encodeURIComponent(String(message || '').trim());
  return `https://wa.me/${num}${text ? `?text=${text}` : ''}`;
}

export function resolveFutbolSupport(user = null) {
  const phone = normalizePhone(
    user?.futbol_branding?.support_whatsapp || FUTBOL_SUPPORT_PHONE,
  );
  const message = DEFAULT_MESSAGES.futbol;
  return {
    support_whatsapp: phone,
    support_name: user?.futbol_branding?.brand_name || 'Soporte futbol.app',
    support_message: message,
    support_activo: true,
    wa_url: buildWaMeUrl(phone, message),
    product: 'futbol',
  };
}

export function resolvePlanSupport(plan) {
  if (!plan) {
    return {
      support_whatsapp: DEFAULT_SUPPORT_PHONE,
      support_name: 'Soporte Fit Live',
      support_message: DEFAULT_MESSAGES.d28d,
      support_activo: true,
      wa_url: buildWaMeUrl(DEFAULT_SUPPORT_PHONE, DEFAULT_MESSAGES.d28d),
    };
  }
  const active = plan.support_activo !== false;
  const phone = active
    ? normalizePhone(plan.support_whatsapp || DEFAULT_SUPPORT_PHONE)
    : DEFAULT_SUPPORT_PHONE;
  const message = defaultMessageForPlan(plan);
  return {
    support_whatsapp: phone,
    support_name: plan.support_name || 'Soporte',
    support_message: message,
    support_activo: active,
    wa_url: buildWaMeUrl(phone, message),
  };
}

export function pickSupportWhatsApp({ trainer, gym, planSupport, sedeFirst = false } = {}) {
  const gymWa = String(gym?.support_whatsapp || '').trim();
  const trainerWa = String(trainer?.support_whatsapp || '').trim();
  const planWa = String(planSupport?.support_whatsapp || '').trim();
  const usableGym = gymWa && !isPlaceholderPhone(gymWa) ? gymWa : '';
  const usableTrainer = trainerWa && !isPlaceholderPhone(trainerWa) ? trainerWa : '';
  const usablePlan = planWa && !isPlaceholderPhone(planWa) ? planWa : '';
  const isSede = sedeFirst || gym?.parent_gym_id != null;
  if (isSede) {
    if (usableGym) return normalizePhone(usableGym);
    return null;
  }
  if (usableTrainer) return normalizePhone(usableTrainer);
  if (usableGym) return normalizePhone(usableGym);
  if (usablePlan) return normalizePhone(usablePlan);
  return null;
}

export function resolveConsumerSupportWaUrl({ planSupport, trainer, gym, sedeFirst = false, user = null } = {}) {
  if (isFutbolOnlyConsumer(user)) return resolveFutbolSupport(user).wa_url;
  const isSede = sedeFirst || gym?.parent_gym_id != null;
  // No cortocircuitar con planSupport.wa_url (suele ser Nicolás) si hay sede/coach.
  if (isSede || gym?.support_whatsapp || trainer?.support_whatsapp) {
    return buildSupportWaUrl({ trainer, gym, planSupport, sedeFirst: isSede });
  }
  if (planSupport?.wa_url) return planSupport.wa_url;
  return buildSupportWaUrl({ trainer, gym, planSupport, sedeFirst });
}

export function buildSupportWaUrl({ trainer, gym, planSupport, sedeFirst = false, user = null } = {}) {
  if (isFutbolOnlyConsumer(user)) return resolveFutbolSupport(user).wa_url;
  const isSedeGym = sedeFirst || gym?.parent_gym_id != null;
  if (planSupport?.wa_url && !trainer?.support_whatsapp && !gym?.support_whatsapp && !isSedeGym) {
    return planSupport.wa_url;
  }
  const phone = pickSupportWhatsApp({ trainer, gym, planSupport, sedeFirst: isSedeGym });
  const message = (isSedeGym || gym?.support_whatsapp) && gym?.welcome_message
    ? String(gym.welcome_message).trim()
    : (planSupport?.support_message || DEFAULT_MESSAGES.d28d);
  if (phone) return buildWaMeUrl(phone, message);
  // Sede sin WA configurado: no caer a Nicolás; usar gym vacío → mensaje sede.
  if (isSedeGym) return null;
  return planSupport?.wa_url || buildWaMeUrl(DEFAULT_SUPPORT_PHONE, message);
}
